import { faArrowLeftLong } from "@fortawesome/free-solid-svg-icons"
import React, { useEffect, useState } from "react"
import Header from "../../../components/navigation/Header"
import Footer from "../../../components/navigation/Footer"
import GetCSFR from "../../../hooks/userAuthentication/GetCSFR"
import GaugeChart from 'react-gauge-chart'
import axios from "axios"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useNavigate } from "react-router-dom"

interface MonthlyInsightData {
    totalTransactions: number;
    averageOrderValue: number;
    isCoach: boolean;
    totalCustomers?: number;
    newChats?: number;
}

interface MonthlyAmountData {
    monthlySpent?: number;
    monthlyEarned?: number;
}

interface StripeOnboardingData {
    isCoach: boolean;
    accountExists: boolean;
    paymentLink: string;
}

export default function AccountInformation(){
    const csrfToken = GetCSFR({ name: "csrftoken" })
    const navigate = useNavigate()
    const [monthlyInsights, setMonthlyInsights] = useState<MonthlyInsightData>()
    const [monthlyAmount, setMonthlyAmount] = useState<MonthlyAmountData>()
    const [stripeOnboarding, setStripeOnboarding] = useState<StripeOnboardingData>()
    const [gaugeValue, setGaugeValue] = useState<number>()
    const date = new Date()
    const months = ["January","February","March","April","May","June","July","August","September","October","November","December"]

    useEffect(()=>{
        // API call to get amount of money spent/earned by athlete/coach
        axios.get(`${process.env.REACT_APP_SKILLJA_URL}/stripe/get_monthly_amount/`,{
            headers: {
                'X-CSRFToken': csrfToken,
                'Content-Type': 'application/json'
            },
            withCredentials: true
        })
        .then(res => {
            if(res.status === 200){
                setMonthlyAmount(res.data)
            }
        })
        .catch(error =>{console.error(error)})

        // API call to get monthly insights about user
        axios.get(`${process.env.REACT_APP_SKILLJA_URL}/stripe/get_monthly_insights/`,{
            headers: {
                'X-CSRFToken': csrfToken,
                'Content-Type': 'application/json'
            },
            withCredentials: true
        })
        .then(res => {
            if(res.status === 200){
                setMonthlyInsights(res.data)
                setGaugeValue(calculateGaugeValue(res.data))
            }
        })
        .catch(error =>{console.error(error)})

        // API call to get check if user has setup their stripe payment profile
        axios.get(`${process.env.REACT_APP_SKILLJA_URL}/stripe/create_stripe_onboarding/`,{
            headers: {
                'X-CSRFToken': csrfToken,
                'Content-Type': 'application/json'
            },
            withCredentials: true
        })
        .then(res => {
            if(res.status === 200){
                setStripeOnboarding(res.data)
            }
        })
        .catch(error =>{console.error(error)})
    },[])

    // Calculate gauge value based on differing criteria
    function calculateGaugeValue(data:MonthlyInsightData){
        // Coaches are judged based on total transactions
        if(data.isCoach){
            return data.totalTransactions <= 40 ? ( data.totalTransactions <= 10 ? 0.13 : 0.47) : 0.87
        } 
        // Athletes are judged based on total transactions and # of new coaches they interaced with
        else {
            const scoreValue = data.totalTransactions * (data.newChats && data.newChats > 0 ? ( 1 + (data.newChats / 100) ) : 1)
            return scoreValue <= 2 ? ( scoreValue <= 1.5 ? 0.13 : 0.47) : 0.87
        }
    }

    return (
        <>
            <Header useCase="protected" />
            <section className="flex flex-col justify-start items-start font-kulim py-2 px-4 lg:px-20 mx-auto mt-10 mb-24">
                <FontAwesomeIcon 
                    icon={faArrowLeftLong} 
                    className="text-3xl hover:text-main-green-500 cursor-pointer mb-8"
                    onClick={()=> navigate('../')}
                />
                <h1 className="text-3xl font-kulim mb-3">
                    Payments
                </h1>
                <p>
                    View monthly statements regarding transactions and payouts.
                </p>
                <p className="mt-8 text-center">
                    To start recieving payments you must complete your Stripe Payment Profile!
                </p>
                <button 
                    onClick={()=>window.open(`${stripeOnboarding?.paymentLink}`)}
                    className={`form-btn text-sm px-3 py-2 mt-3 mb-0 rounded-xl`}
                    disabled={!stripeOnboarding?.paymentLink}
                >
                    {stripeOnboarding && stripeOnboarding.accountExists ? 'View Stripe Profile' : 'Start Receiving Payments'}
                </button>
                <section className="flex flex-wrap w-full mt-16">
                    <div className="my-4 lg:mr-4 xl:mr-12 p-5 w-full max-w-md border border-main-grey-100 bg-white rounded-xl font-kulim">
                        <h1 className="text-xl font-semibold">
                            {months[date.getMonth()]}
                        </h1>
                        <p className="mt-2 mb-8">
                            {monthlyInsights?.isCoach ?
                                // `Your next payout will be sent via email on ${date.getMonth() === 11 ? 'January 1' : `${months[date.getMonth()+1]} 1`}`
                                `During ${months[date.getMonth()]} you earned a total of:`
                            :
                                `During ${months[date.getMonth()]} you spent a total of:`
                            }   
                        </p>
                        <h2 className="text-5xl font-semibold mb-2">
                            $ {monthlyInsights ? (monthlyInsights?.isCoach ? monthlyAmount?.monthlyEarned : monthlyAmount?.monthlySpent) : '0'}
                        </h2>
                    </div>
                    <div className="flex flex-wrap my-4 p-5 border border-main-grey-100 bg-white rounded-xl font-kulim">
                        <div className="mb-6 md:mr-4 xl:mr-12">
                            <h1 className="text-xl font-semibold mb-6">
                                Monthly Insights
                            </h1>
                            {monthlyInsights?.isCoach ?
                            <>
                                <p className="my-2">
                                    • You sold {monthlyInsights ? monthlyInsights?.totalTransactions : '0'} service{monthlyInsights && monthlyInsights.totalTransactions === 1 ? '' : 's'}.
                                </p>
                                <p className="my-2">
                                    • Your average order value was ${monthlyInsights ? monthlyInsights?.averageOrderValue : '0'}
                                </p>
                                <p className="my-2">
                                    • You had {monthlyInsights ? monthlyInsights?.totalCustomers : '0'} unique customer{monthlyInsights && monthlyInsights?.totalCustomers === 1 ? '' :'s'}!
                                </p>
                            </>
                            :
                            <>
                                <p className="my-2">
                                    • You made {monthlyInsights ? monthlyInsights?.totalTransactions : '1'} purchase{monthlyInsights && monthlyInsights.totalTransactions === 1 ? '' :'s'}.
                                </p>
                                <p className="my-2">
                                    • Your average order value was ${monthlyInsights ? monthlyInsights?.averageOrderValue : '0'}.
                                </p>
                                <p className="my-2">
                                    • You interacted with {monthlyInsights ? monthlyInsights?.newChats : '0'} new coach{monthlyInsights && monthlyInsights.newChats === 1 ? '': 'es'}!
                                </p>
                            </>
                            }
                        </div>
                        <GaugeChart id="gauge-chart" 
                            nrOfLevels={3} 
                            animateDuration={4000}
                            hideText={true} 
                            percent={gaugeValue ? gaugeValue : 0} 
                            colors={['#e3e3e3', '#b1d5b6','#507656']}
                            style={{width:'240px', margin:'auto', padding:'0px'}}
                        />
                    </div>
                </section>
            </section>
            <Footer />
        </>
    )
}